import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  TablePagination,
  Skeleton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Business as BusinessIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'auto',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease-in-out',
  },
  '&:last-child td': {
    border: 0,
  },
  '& .MuiTableCell-root': {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const TrendIcon = ({ trend }) => {
  const props = { sx: { fontSize: 20 } };
  switch (trend) {
    case 'improving': return <TrendingUpIcon color="success" {...props} />;
    case 'declining': return <TrendingDownIcon color="error" {...props} />;
    case 'stable': return <TrendingFlatIcon color="info" {...props} />;
    default: return <TrendingFlatIcon color="disabled" {...props} />;
  }
};

// Helper function to get color based on rank value
const getRankColor = (rank) => {
  if (rank <= 3) return 'success';
  else if (rank <= 10) return 'warning';
  else if (rank > 10) return 'error';
  return 'default';
};

// Helper function to get MUI color value for Typography
const getRankColorValue = (rank) => {
  const colorName = getRankColor(rank);
  const colorMap = {
    'success': '#4caf50',
    'warning': '#ff9800',
    'error': '#f44336',
    'default': '#999'
  };
  return colorMap[colorName];
};

const RankChip = ({ rank }) => {
  const color = getRankColor(rank);

  return (
    <Chip
      label={`#${rank}`}
      color={color}
      size="small"
      sx={{ minWidth: 50, fontWeight: 'bold' }}
    />
  );
};

const AgencyKeywordTable = () => {
  const [keywordData, setKeywordData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ 
    page: 1, 
    pageSize: 500, 
    total: 0, 
    totalPages: 0 
  });
  const [sortBy, setSortBy] = useState('rank');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [trendFilter, setTrendFilter] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loadKeywordData = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post('/api/agency/keywords/detailed', {
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder,
        search: searchTerm,
        trendFilter,
        rankFilter
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.data.success) {
        setKeywordData(response.data.data.keywords);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.total,
          totalPages: response.data.data.totalPages
        }));
      }
    } catch (error) {
      console.error('Error loading keyword data:', error);
      setKeywordData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeywordData();
  }, [pagination.page, pagination.pageSize, sortBy, sortOrder, searchTerm, trendFilter, rankFilter]);

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortBy(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handlePageSizeChange = (event) => {
    setPagination(prev => ({ 
      ...prev, 
      pageSize: parseInt(event.target.value), 
      page: 1 
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTrendFilter('');
    setRankFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const columns = [
    {
      id: 'keyword',
      label: 'Keyword',
      sortable: true,
      minWidth: 200,
      render: (row) => (
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {row.keyword}
        </Typography>
      )
    },
    {
      id: 'businessName',
      label: 'Unternehmen',
      sortable: true,
      minWidth: 180,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main', 
            width: 32, 
            height: 32 
          }}>
            <BusinessIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.businessName}
            </Typography>
            {row.businessCity && (
              <Typography variant="caption" color="text.secondary">
                📍 {row.businessCity}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      id: 'rank',
      label: 'Ranking',
      sortable: true,
      minWidth: 100,
      align: 'center',
      render: (row) => (
        <RankChip rank={row.rank || 100} />
      )
    },
    {
      id: 'rankChange',
      label: 'Ranking Änderung',
      sortable: true,
      minWidth: 140,
      align: 'center',
      render: (row) => {
        // Check if we have a valid rankChange value
        const hasValidRankChange = row.rankChange !== null && row.rankChange !== undefined && row.rankChange !== 0 || row.rankChange === 0;

        // Show explicit message ONLY if insufficient data AND no rankChange available
        if (!row.hasEnoughData && !hasValidRankChange) {
          return (
            <Tooltip title={row.dataMessage || 'Nicht genug Daten für Vergleich'}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {row.dataMessage || '—'}
              </Typography>
            </Tooltip>
          );
        }

        // Determine color based on ranking change
        const changeColor = row.rankChange > 0 ? '#4caf50' : row.rankChange < 0 ? '#f44336' : '#999';
        const changeSign = row.rankChange > 0 ? '+' : '';

        // Show "+5" or "-5" format with color coding
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <TrendIcon trend={row.trend} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: changeColor,
                fontSize: '1rem'
              }}
            >
              {changeSign}{row.rankChange ?? '—'}
            </Typography>
          </Box>
        );
      }
    },
    {
      id: 'lastUpdated',
      label: 'Zuletzt aktualisiert',
      sortable: true,
      minWidth: 140,
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.lastUpdated ? new Date(row.lastUpdated).toLocaleDateString('de-DE') : 'Nie'}
        </Typography>
      )
    }
  ];

  const LoadingSkeleton = ({ rows = 5 }) => (
    <>
      {[...Array(rows)].map((_, index) => (
        <TableRow key={index}>
          {columns.map((column) => (
            <TableCell key={column.id} align={column.align}>
              <Skeleton variant="rectangular" height={20} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  const EmptyState = () => (
    <TableRow>
      <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keine Keywords gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Versuchen Sie eine andere Suche oder passen Sie die Filter an.
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: filtersOpen ? 2 : 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: filtersOpen ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <TextField
                placeholder="Keywords oder Unternehmen suchen..."
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                onClick={() => setFiltersOpen(!filtersOpen)}
                sx={{ 
                  color: (trendFilter || rankFilter) ? 'primary.main' : 'inherit'
                }}
              >
                <FilterListIcon />
                {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={filtersOpen}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Trend Filter</InputLabel>
                  <Select
                    value={trendFilter}
                    onChange={(e) => setTrendFilter(e.target.value)}
                    label="Trend Filter"
                  >
                    <MenuItem value="">Alle Trends</MenuItem>
                    <MenuItem value="improving">Steigend</MenuItem>
                    <MenuItem value="declining">Fallend</MenuItem>
                    <MenuItem value="stable">Stabil</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Ranking Filter</InputLabel>
                  <Select
                    value={rankFilter}
                    onChange={(e) => setRankFilter(e.target.value)}
                    label="Ranking Filter"
                  >
                    <MenuItem value="">Alle Rankings</MenuItem>
                    <MenuItem value="top3">Top 3 (1-3)</MenuItem>
                    <MenuItem value="top10">Top 10 (1-10)</MenuItem>
                    <MenuItem value="top20">Top 20 (1-20)</MenuItem>
                    <MenuItem value="poor">Schlecht (20)</MenuItem>
                    <MenuItem value="big_improvement">Große Verbesserung (+5 oder mehr)</MenuItem>
                    <MenuItem value="improvement">Verbesserung (positiv)</MenuItem>
                    <MenuItem value="decline">Verschlechterung (negativ)</MenuItem>
                    <MenuItem value="big_decline">Große Verschlechterung (-5 oder mehr)</MenuItem>
                    <MenuItem value="no_change">Keine Änderung (0)</MenuItem>
                    <MenuItem value="with_change">Nur mit Änderung (!= 0)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography 
                  variant="button" 
                  sx={{ 
                    cursor: 'pointer',
                    color: 'primary.main',
                    textDecoration: 'underline',
                    display: 'block',
                    mt: 1
                  }}
                  onClick={clearFilters}
                >
                  Filter zurücksetzen
                </Typography>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Keyword Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <StyledTableContainer sx={{ maxHeight: '70vh', minHeight: '400px' }}>
          <Table stickyHeader>
            <StyledTableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortOrder : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <LoadingSkeleton />
              ) : keywordData.length === 0 ? (
                <EmptyState />
              ) : (
                keywordData.map((row, index) => (
                  <StyledTableRow key={`${row.businessId}-${row.keyword}-${index}`}>
                    {columns.map((column) => (
                      <TableCell key={column.id} align={column.align}>
                        {column.render ? column.render(row) : row[column.id]}
                      </TableCell>
                    ))}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onRowsPerPageChange={handlePageSizeChange}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              minHeight: 56
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default AgencyKeywordTable;